import React, { useEffect, useMemo, useState } from "react";
import {
  Box,
  Grid,
  Typography,
  Button,
  Stack,
  TextField,
  IconButton,
  Avatar,
  Tooltip,
} from "@mui/material";
import {
  LocalizationProvider,
  DatePicker,
  TimePicker,
} from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { FiPlus, FiEdit2, FiTrash2, FiClock, FiCheck, FiX } from "react-icons/fi";
import dayjs from "dayjs";
import {
  formatDisplayDate,
  formatDisplayTime,
  calculateDuration,
} from "../utils/helpers";
import { VERIFY_STATUS } from "../../../constant/constant";
import { visibleAssignees, canSeeAssignees } from "../../../components/AssigneeGroup/utils";
import { getTokenbyData } from "../../../services/user.service";
import ConfirmDialog from "../../../sharedComponents/ConfirmDialog/ConfirmDialog";

// ── Design tokens (match TaskDetails + DetailsPanel) ─────────────────────────
const UI = {
  bg: "#FFFFFF",
  surface: "#FAFBFC",
  border: "#E5E7EB",
  borderLight: "#F1F2F4",
  text: "#0F172A",
  textSub: "#475569",
  textMuted: "#94A3B8",
  accent: "#0D9488",
  accentSoft: "#F0FDFA",
  hover: "#F9FAFB",
  edited: "#0D9488",
};

const nonEmpty = (s) => typeof s === "string" && s.trim().length > 0;

const WorkLogs = ({
  workLogsData,
  selectedData,
  updateTaskData,
  isAdmin,
}) => {
  const [workLogs, setWorkLogs] = useState(workLogsData || []);

  const [editingId, setEditingId] = useState(null);
  const [editDate, setEditDate] = useState(null);
  const [editStart, setEditStart] = useState(null);
  const [editEnd, setEditEnd] = useState(null);

  const [adding, setAdding] = useState(false);
  const [newDate, setNewDate] = useState(null);
  const [newStart, setNewStart] = useState(null);
  const [newEnd, setNewEnd] = useState(null);

  const [confirmTarget, setConfirmTarget] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const [addError, setAddError] = useState(null);
  const [editError, setEditError] = useState(null);

  const [userId, setUserId] = useState(null);

  useEffect(() => {
    setWorkLogs(workLogsData || []);
  }, [workLogsData]);

  useEffect(() => {
    (async () => {
      try {
        const res = await getTokenbyData();
        setUserId(res?.data?.userId || null);
      } catch (e) {
        // silent
      }
    })();
  }, []);

  // Who this entry's hours belong to. `assignedBy` is always the person doing
  // the logging; `assignedTo` is whose work it is, and the two differ whenever
  // a manager records time on someone's behalf.
  //
  // This used to be `selectedData.assignee` — the LEGACY SCALAR MIRROR, which
  // is just assignees[0].user. On a multi-assignee task that pinned every entry
  // a privileged viewer added to whoever happened to be first in the roster:
  // an admin could not log their own hours, nor log against the second
  // assignee. (A pure assignee was unaffected only by accident — the private
  // redactor repoints that mirror at them.)
  const roster = useMemo(() => visibleAssignees(selectedData, {
    currentUserId: userId,
    isAdmin,
  }), [selectedData, userId, isAdmin]);

  // Privileged viewers choose; everyone else can only ever log their own time,
  // so they get no picker and no choice.
  const canChooseTarget = useMemo(
    () => canSeeAssignees(selectedData, { currentUserId: userId, isAdmin }),
    [selectedData, userId, isAdmin]
  );

  // Themselves when they are on the roster — an admin adding a log is usually
  // logging their own hours. Otherwise the first assignee, as before.
  const defaultTargetId = useMemo(() => {
    if (userId && roster.some((a) => String(a._id) === String(userId))) return String(userId);
    if (roster.length) return String(roster[0]._id);
    const v = selectedData?.assignedTo || selectedData?.assignee || userId;
    return v && typeof v === "object" ? String(v._id || "") : (v ? String(v) : "");
  }, [roster, selectedData, userId]);

  const [newTarget, setNewTarget] = useState("");
  // The target the add-row is actually using: an explicit pick, else the default.
  const taskAssigneeId = newTarget || defaultTargetId;

  // An admin who is not on the roster still needs to appear as a choice when
  // the default fell back to them.
  const targetOptions = useMemo(() => {
    const out = roster.map((a) => ({ id: String(a._id), name: a.name }));
    if (userId && !out.some((o) => o.id === String(userId))) {
      out.unshift({ id: String(userId), name: "Me" });
    }
    return out;
  }, [roster, userId]);

  // Frontend overlap detection
  const findOverlap = (newStartDay, newEndDay, excludeId = null) => {
    const target = taskAssigneeId;
    if (!target || !newStartDay || !newEndDay) return null;
    return workLogs.find((log) => {
      if (excludeId && log._id === excludeId) return false;
      if (String(log.assignedTo) !== String(target)) return false;
      const ls = dayjs(log.startTime);
      const le = dayjs(log.endTime);
      if (!dayjs(newStartDay).isSame(ls, "day")) return false;
      return (
        dayjs(newStartDay).isBefore(le) && dayjs(newEndDay).isAfter(ls)
      );
    });
  };

  const anchorToDate = (date, time) =>
    date
      .hour(time.hour())
      .minute(time.minute())
      .second(0)
      .millisecond(0);

  // ── Handlers ──────────────────────────────────────────────────────────────
  const startAdd = () => {
    setAdding(true);
    setNewTarget(defaultTargetId);
    setNewDate(dayjs());
    setNewStart(dayjs().startOf("hour"));
    setNewEnd(dayjs().startOf("hour").add(1, "hour"));
  };

  const cancelAdd = () => {
    setAdding(false);
    setNewTarget("");
    setNewDate(null);
    setNewStart(null);
    setNewEnd(null);
    setAddError(null);
  };

  const saveAdd = async () => {
    setAddError(null);
    if (!newDate || !newStart || !newEnd) {
      setAddError("Please fill all fields");
      return;
    }
    if (newEnd.isBefore(newStart)) {
      setAddError("End time cannot be before start time");
      return;
    }
    const s = anchorToDate(newDate, newStart);
    const e = anchorToDate(newDate, newEnd);
    const conflict = findOverlap(s, e);
    if (conflict) {
      setAddError(
        `Time overlap with existing log (${dayjs(conflict.startTime).format("hh:mm A")} - ${dayjs(conflict.endTime).format("hh:mm A")})`
      );
      return;
    }

    const entry = {
      date: s.toISOString(),
      startTime: s.toISOString(),
      endTime: e.toISOString(),
      assignedBy: userId,
      assignedTo: taskAssigneeId,
      addedAt: dayjs().toISOString(),
    };
    if (selectedData?.verifyStatus === VERIFY_STATUS.REOPEN) {
      entry.verifyStatus = VERIFY_STATUS.REOPEN;
    } else if (selectedData?.clientVerifyStatus === VERIFY_STATUS.REOPEN) {
      entry.clientVerifyStatus = VERIFY_STATUS.REOPEN;
    }
    const result = await updateTaskData(
      { workLog: JSON.parse(JSON.stringify([...workLogs, entry])) },
      { silent: true }
    );
    if (result.ok) {
      cancelAdd();
    } else {
      setAddError(result.message || "Failed to save work log");
    }
  };

  const startEdit = (log) => {
    setEditingId(log._id);
    setEditDate(dayjs(log.date));
    setEditStart(dayjs(log.startTime));
    setEditEnd(dayjs(log.endTime));
  };

  const cancelEdit = () => {
    setEditingId(null);
    setEditDate(null);
    setEditStart(null);
    setEditEnd(null);
    setEditError(null);
  };

  const saveEdit = async (logId) => {
    setEditError(null);
    if (!editDate || !editStart || !editEnd) {
      setEditError("Please fill all fields");
      return;
    }
    if (editEnd.isBefore(editStart)) {
      setEditError("End time cannot be before start time");
      return;
    }
    const s = anchorToDate(editDate, editStart);
    const e = anchorToDate(editDate, editEnd);
    const conflict = findOverlap(s, e, logId);
    if (conflict) {
      setEditError(
        `Time overlap with existing log (${dayjs(conflict.startTime).format("hh:mm A")} - ${dayjs(conflict.endTime).format("hh:mm A")})`
      );
      return;
    }
    const updated = workLogs.map((log) =>
      log._id === logId
        ? {
            ...log,
            date: s.toISOString(),
            startTime: s.toISOString(),
            endTime: e.toISOString(),
            assignedTo: log.assignedTo || taskAssigneeId,
            editedBy: userId,
            editedAt: dayjs().toISOString(),
          }
        : log
    );
    const result = await updateTaskData(
      { workLog: JSON.parse(JSON.stringify(updated)) },
      { silent: true }
    );
    if (result.ok) {
      cancelEdit();
    } else {
      setEditError(result.message || "Failed to update work log");
    }
  };

  const requestDelete = (log) => setConfirmTarget(log);

  const confirmDelete = async () => {
    if (!confirmTarget) return;
    setDeleting(true);
    try {
      const updated = workLogs.filter((l) => l._id !== confirmTarget._id);
      const result = await updateTaskData(
        { workLog: JSON.parse(JSON.stringify(updated)) },
        { silent: true }
      );
      if (result.ok) setConfirmTarget(null);
    } catch (err) {
      console.error(err);
    } finally {
      setDeleting(false);
    }
  };

  // ── Reusable UI fragments ─────────────────────────────────────────────────
  const toId = (v) =>
    v && typeof v === "object" ? String(v._id || "") : String(v || "");

  const canEdit = (log) =>
    isAdmin ||
    toId(log.assignedTo) === String(userId || "") ||
    toId(log.assignedBy) === String(userId || "") ||
    toId(log.editedBy) === String(userId || "");

  // Compact, enterprise-style picker — no labels, no underline
  const pickerSx = {
    "& .MuiOutlinedInput-root": {
      borderRadius: "6px",
      fontSize: "12.5px",
      background: UI.bg,
      "& fieldset": { borderColor: UI.border },
      "&:hover fieldset": { borderColor: UI.textMuted },
      "&.Mui-focused fieldset": { borderColor: UI.accent, borderWidth: "1px" },
    },
    "& .MuiInputBase-input": { py: 0.8 },
  };

  const totalDuration = useMemo(() => {
    let minutes = 0;
    for (const l of workLogs) {
      if (l.startTime && l.endTime) {
        minutes += dayjs(l.endTime).diff(dayjs(l.startTime), "minute");
      }
    }
    const h = Math.floor(minutes / 60);
    const m = minutes % 60;
    return `${h}h ${m}m`;
  }, [workLogs]);

  // ── Render ────────────────────────────────────────────────────────────────
  return (
    <Box
      sx={{
        border: `1px solid ${UI.border}`,
        borderRadius: "12px",
        overflow: "hidden",
        background: UI.bg,
        display: "flex",
        flexDirection: "column",
        maxHeight: 480,
      }}
    >
      {/* Toolbar */}
      <Stack
        direction="row"
        alignItems="center"
        justifyContent="space-between"
        sx={{
          px: 2,
          py: 1.5,
          borderBottom: `1px solid ${UI.borderLight}`,
          background: UI.surface,
          flexShrink: 0,
        }}
      >
        <Stack direction="row" spacing={1.5} alignItems="center">
          <Typography sx={{ fontSize: "12px", color: UI.textMuted }}>
            Total tracked
          </Typography>
          <Typography sx={{ fontSize: "13px", fontWeight: 700, color: UI.text }}>
            {totalDuration}
          </Typography>
          <Typography sx={{ fontSize: "12px", color: UI.textMuted }}>
            · {workLogs.length} {workLogs.length === 1 ? "entry" : "entries"}
          </Typography>
        </Stack>
        {!adding && (
          <Button
            onClick={startAdd}
            startIcon={<FiPlus size={14} />}
            sx={{
              textTransform: "none",
              background: UI.accent,
              color: "#fff",
              fontWeight: 600,
              fontSize: "12.5px",
              px: 1.8,
              py: 0.6,
              borderRadius: "8px",
              boxShadow: "none",
              "&:hover": { background: "#0F766E", boxShadow: "none" },
            }}
          >
            Add Work Log
          </Button>
        )}
      </Stack>

      {/* Inline add form — pinned below toolbar, not in scroll */}
      {adding && (
        <Box
          sx={{
            borderBottom: `1px solid ${UI.border}`,
            background: UI.surface,
            p: 2,
            flexShrink: 0,
          }}
        >
          <Typography
            sx={{
              fontSize: "11px",
              fontWeight: 700,
              color: UI.textMuted,
              textTransform: "uppercase",
              letterSpacing: "0.06em",
              mb: 1.2,
            }}
          >
            New work log
          </Typography>
          {/* Only a privileged viewer chooses. For everyone else the target is
              always themselves, so a picker would be a one-item menu. */}
          {canChooseTarget && targetOptions.length > 1 && (
            <TextField
              select
              fullWidth
              size="small"
              label="Hours for"
              value={taskAssigneeId || ""}
              onChange={(ev) => setNewTarget(ev.target.value)}
              SelectProps={{ native: true }}
              InputLabelProps={{ shrink: true }}
              sx={{ ...pickerSx, mb: 1.5 }}
            >
              {targetOptions.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.id === String(userId) && o.name !== "Me" ? `${o.name} (me)` : o.name}
                </option>
              ))}
            </TextField>
          )}
          <LocalizationProvider dateAdapter={AdapterDayjs}>
            <Grid container spacing={1.5}>
              <Grid item xs={12} sm={4}>
                <DatePicker
                  value={newDate}
                  onChange={setNewDate}
                  slotProps={{ textField: { fullWidth: true, size: "small", sx: pickerSx } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TimePicker
                  value={newStart}
                  onChange={setNewStart}
                  slotProps={{ textField: { fullWidth: true, size: "small", sx: pickerSx } }}
                />
              </Grid>
              <Grid item xs={12} sm={4}>
                <TimePicker
                  value={newEnd}
                  onChange={setNewEnd}
                  slotProps={{ textField: { fullWidth: true, size: "small", sx: pickerSx } }}
                />
              </Grid>
            </Grid>
          </LocalizationProvider>
          {addError && (
            <Box
              sx={{
                mt: 1.5,
                px: 1.5,
                py: 1,
                borderRadius: "8px",
                background: "#FEF2F2",
                border: "1px solid #FECACA",
                display: "flex",
                alignItems: "flex-start",
                gap: 1,
              }}
            >
              <Typography sx={{ fontSize: "12.5px", color: "#DC2626", flex: 1, lineHeight: 1.6 }}>
                {addError}
              </Typography>
              <IconButton
                size="small"
                onClick={() => setAddError(null)}
                sx={{ p: 0, mt: 0.2, color: "#DC2626", flexShrink: 0 }}
              >
                <FiX size={13} />
              </IconButton>
            </Box>
          )}
          <Stack direction="row" spacing={1} justifyContent="flex-end" sx={{ mt: 1.5 }}>
            <Button
              onClick={cancelAdd}
              sx={{
                textTransform: "none",
                color: UI.textSub,
                fontWeight: 600,
                fontSize: "12.5px",
                border: `1px solid ${UI.border}`,
                background: UI.bg,
                px: 1.8,
                py: 0.5,
                borderRadius: "8px",
                "&:hover": { background: UI.hover },
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={saveAdd}
              sx={{
                textTransform: "none",
                background: UI.accent,
                color: "#fff",
                fontWeight: 600,
                fontSize: "12.5px",
                px: 2,
                py: 0.5,
                borderRadius: "8px",
                boxShadow: "none",
                "&:hover": { background: "#0F766E", boxShadow: "none" },
              }}
            >
              Save
            </Button>
          </Stack>
        </Box>
      )}

      {/* Scrollable log list */}
      <Box sx={{ overflowY: "auto", flexGrow: 1, p: 2 }}>

      {/* Empty state */}
      {workLogs.length === 0 && !adding && (
        <Stack alignItems="center" spacing={1} sx={{ py: 5 }}>
          <Box
            sx={{
              width: 44,
              height: 44,
              borderRadius: "50%",
              background: UI.accentSoft,
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              color: UI.accent,
            }}
          >
            <FiClock size={20} />
          </Box>
          <Typography sx={{ fontSize: "13.5px", fontWeight: 600 }}>
            No work logs yet
          </Typography>
          <Typography sx={{ fontSize: "12px", color: UI.textMuted }}>
            Track time spent on this task.
          </Typography>
        </Stack>
      )}

      {/* Desktop table */}
      {workLogs.length > 0 && (
        <Box
          sx={{
            display: { xs: "none", md: "block" },
            border: `1px solid ${UI.border}`,
            borderRadius: "10px",
            overflow: "hidden",
            background: UI.bg,
          }}
        >
          {/* Header */}
          <Box
            sx={{
              display: "grid",
              gridTemplateColumns: "2fr 1.2fr 1fr 1fr 0.8fr 100px",
              gap: 1,
              px: 2,
              py: 1.2,
              borderBottom: `1px solid ${UI.borderLight}`,
              background: UI.surface,
            }}
          >
            {["Assignee", "Date", "Start", "End", "Duration", ""].map((h, i) => (
              <Typography
                key={i}
                sx={{
                  fontSize: "11px",
                  fontWeight: 700,
                  color: UI.textMuted,
                  letterSpacing: "0.06em",
                  textTransform: "uppercase",
                  textAlign: i === 5 ? "right" : "left",
                }}
              >
                {h}
              </Typography>
            ))}
          </Box>

          {/* Rows */}
          {workLogs.map((log) => (
            <Box
              key={log._id || `${log.date}-${log.startTime}`}
              sx={{
                px: 2,
                py: editingId === log._id ? 1.5 : 1.3,
                borderBottom: `1px solid ${UI.borderLight}`,
                "&:last-of-type": { borderBottom: "none" },
                transition: "background-color 0.12s",
                "&:hover": { background: UI.hover },
              }}
            >
              {editingId === log._id ? (
                <Box
                  sx={{
                    background: "#F8FAFC",
                    border: `1.5px solid ${UI.accent}30`,
                    borderRadius: "10px",
                    p: 1.5,
                  }}
                >
                  {/* Row 1: Assignee + action buttons */}
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1.2}>
                    <Stack direction="row" spacing={1.2} alignItems="center">
                      <Avatar
                        sx={{
                          width: 26, height: 26, fontSize: "11px", fontWeight: 700,
                          background: UI.accent, color: "#fff", flexShrink: 0,
                        }}
                      >
                        {log.assignedToName?.charAt(0)?.toUpperCase() || "?"}
                      </Avatar>
                      <Typography sx={{ fontSize: "13px", fontWeight: 600, color: UI.text }}>
                        {log.assignedToName || "N/A"}
                      </Typography>
                      {editStart && editEnd && (
                        <Box
                          sx={{
                            px: "8px", py: "2px", borderRadius: "20px",
                            background: UI.accentSoft, border: `1px solid ${UI.accent}30`,
                          }}
                        >
                          <Typography sx={{ fontSize: "12px", fontWeight: 700, color: UI.accent }}>
                            {calculateDuration(editStart.toISOString(), editEnd.toISOString())}
                          </Typography>
                        </Box>
                      )}
                    </Stack>
                    <Stack direction="row" spacing={0.5}>
                      <Tooltip title="Save">
                        <IconButton
                          size="small"
                          onClick={() => saveEdit(log._id)}
                          sx={{
                            background: UI.accent, color: "#fff", borderRadius: "7px",
                            width: 30, height: 30, "&:hover": { background: "#0F766E" },
                          }}
                        >
                          <FiCheck size={14} />
                        </IconButton>
                      </Tooltip>
                      <Tooltip title="Cancel">
                        <IconButton
                          size="small"
                          onClick={cancelEdit}
                          sx={{
                            background: "#E2E8F0", color: UI.textSub, borderRadius: "7px",
                            width: 30, height: 30,
                            "&:hover": { background: "#FEE2E2", color: "#EF4444" },
                          }}
                        >
                          <FiX size={14} />
                        </IconButton>
                      </Tooltip>
                    </Stack>
                  </Stack>

                  {/* Row 2: Date + Start + End pickers — each gets equal space */}
                  <LocalizationProvider dateAdapter={AdapterDayjs}>
                    <Box sx={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 1.2 }}>
                      <Box>
                        <Typography sx={{ fontSize: "10.5px", fontWeight: 600, color: UI.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
                          Date
                        </Typography>
                        <DatePicker
                          value={editDate}
                          onChange={setEditDate}
                          slotProps={{ textField: { fullWidth: true, size: "small", sx: pickerSx } }}
                        />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "10.5px", fontWeight: 600, color: UI.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
                          Start
                        </Typography>
                        <TimePicker
                          value={editStart}
                          onChange={setEditStart}
                          slotProps={{ textField: { fullWidth: true, size: "small", sx: pickerSx } }}
                        />
                      </Box>
                      <Box>
                        <Typography sx={{ fontSize: "10.5px", fontWeight: 600, color: UI.textMuted, textTransform: "uppercase", letterSpacing: "0.06em", mb: 0.5 }}>
                          End
                        </Typography>
                        <TimePicker
                          value={editEnd}
                          onChange={setEditEnd}
                          slotProps={{ textField: { fullWidth: true, size: "small", sx: pickerSx } }}
                        />
                      </Box>
                    </Box>
                  </LocalizationProvider>

                  {editError && (
                    <Box
                      sx={{
                        mt: 1.2, px: 1.5, py: 1, borderRadius: "8px",
                        background: "#FEF2F2", border: "1px solid #FECACA",
                        display: "flex", alignItems: "flex-start", gap: 1,
                      }}
                    >
                      <Typography sx={{ fontSize: "12.5px", color: "#DC2626", flex: 1, lineHeight: 1.6 }}>
                        {editError}
                      </Typography>
                      <IconButton size="small" onClick={() => setEditError(null)} sx={{ p: 0, mt: 0.2, color: "#DC2626", flexShrink: 0 }}>
                        <FiX size={13} />
                      </IconButton>
                    </Box>
                  )}
                </Box>
              ) : (
                <Box
                  sx={{
                    display: "grid",
                    gridTemplateColumns: "2fr 1.2fr 1fr 1fr 0.8fr 100px",
                    gap: 1,
                    alignItems: "center",
                  }}
                >
                  {/* Assignee */}
                  <Stack direction="row" spacing={1.2} alignItems="center" sx={{ minWidth: 0 }}>
                    <Avatar
                      sx={{
                        width: 26,
                        height: 26,
                        fontSize: "11px",
                        fontWeight: 700,
                        background: UI.accent,
                        color: "#fff",
                        flexShrink: 0,
                      }}
                    >
                      {log.assignedToName?.charAt(0)?.toUpperCase() || "?"}
                    </Avatar>
                    <Box sx={{ minWidth: 0 }}>
                      <Typography
                        sx={{
                          fontSize: "13px",
                          fontWeight: 600,
                          color: UI.text,
                          lineHeight: 1.2,
                          whiteSpace: "nowrap",
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                        }}
                      >
                        {log.assignedToName || "N/A"}
                      </Typography>
                      <Stack direction="row" spacing={0.5} alignItems="center" sx={{ mt: 0.2 }}>
                        {nonEmpty(log.assignedByName) && (
                          <Typography sx={{ fontSize: "11px", color: UI.textMuted }}>
                            Added by {log.assignedByName}
                            {log.addedAt && ` · ${dayjs(log.addedAt).format("DD MMM")}`}
                          </Typography>
                        )}
                        {nonEmpty(log.editedByName) && (
                          <Tooltip
                            title={
                              log.editedAt
                                ? `Edited by ${log.editedByName} on ${dayjs(log.editedAt).format("DD MMM YYYY, hh:mm A")}`
                                : `Edited by ${log.editedByName}`
                            }
                          >
                            <Box
                              sx={{
                                ml: 0.5,
                                fontSize: "10px",
                                fontWeight: 600,
                                color: UI.edited,
                                background: UI.accentSoft,
                                border: `1px solid ${UI.edited}30`,
                                px: 0.6,
                                py: 0.1,
                                borderRadius: "4px",
                                lineHeight: 1.4,
                                textTransform: "uppercase",
                                letterSpacing: "0.04em",
                              }}
                            >
                              edited
                            </Box>
                          </Tooltip>
                        )}
                      </Stack>
                    </Box>
                  </Stack>

                  {/* Date */}
                  <Typography sx={{ fontSize: "13px", color: UI.text }}>
                    {formatDisplayDate(log.date)}
                  </Typography>

                  {/* Start */}
                  <Typography sx={{ fontSize: "13px", color: UI.text, fontVariantNumeric: "tabular-nums" }}>
                    {formatDisplayTime(log.startTime)}
                  </Typography>

                  {/* End */}
                  <Typography sx={{ fontSize: "13px", color: UI.text, fontVariantNumeric: "tabular-nums" }}>
                    {formatDisplayTime(log.endTime)}
                  </Typography>

                  {/* Duration */}
                  <Typography
                    sx={{
                      fontSize: "12.5px",
                      fontWeight: 600,
                      color: UI.accent,
                      fontVariantNumeric: "tabular-nums",
                    }}
                  >
                    {calculateDuration(log.startTime, log.endTime)}
                  </Typography>

                  {/* Actions */}
                  <Stack direction="row" spacing={0.4} justifyContent="flex-end">
                    {canEdit(log) && (
                      <Tooltip title="Edit">
                        <IconButton
                          size="small"
                          onClick={() => startEdit(log)}
                          sx={{
                            color: UI.textSub,
                            borderRadius: "6px",
                            "&:hover": { background: UI.accentSoft, color: UI.accent },
                          }}
                        >
                          <FiEdit2 size={14} />
                        </IconButton>
                      </Tooltip>
                    )}
                    {canEdit(log) && (
                      <Tooltip title="Delete">
                        <IconButton
                          size="small"
                          onClick={() => requestDelete(log)}
                          sx={{
                            color: UI.textSub,
                            borderRadius: "6px",
                            "&:hover": { background: "#FEE2E2", color: "#DC2626" },
                          }}
                        >
                          <FiTrash2 size={14} />
                        </IconButton>
                      </Tooltip>
                    )}
                  </Stack>
                </Box>
              )}
            </Box>
          ))}
        </Box>
      )}

      {/* Mobile cards */}
      {workLogs.length > 0 && (
        <Box sx={{ display: { xs: "block", md: "none" } }}>
          <Stack spacing={1.2}>
            {workLogs.map((log) => (
              <Box
                key={log._id || `${log.date}-${log.startTime}`}
                sx={{
                  border: `1px solid ${UI.border}`,
                  borderRadius: "10px",
                  background: UI.bg,
                  p: 1.5,
                }}
              >
                {editingId === log._id ? (
                  <Stack spacing={1.2}>
                    <LocalizationProvider dateAdapter={AdapterDayjs}>
                      <DatePicker
                        value={editDate}
                        onChange={setEditDate}
                        slotProps={{ textField: { fullWidth: true, size: "small", sx: pickerSx } }}
                      />
                      <Stack direction="row" spacing={1}>
                        <TimePicker
                          value={editStart}
                          onChange={setEditStart}
                          slotProps={{ textField: { fullWidth: true, size: "small", sx: pickerSx } }}
                        />
                        <TimePicker
                          value={editEnd}
                          onChange={setEditEnd}
                          slotProps={{ textField: { fullWidth: true, size: "small", sx: pickerSx } }}
                        />
                      </Stack>
                    </LocalizationProvider>
                    {editError && (
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderRadius: "8px",
                          background: "#FEF2F2",
                          border: "1px solid #FECACA",
                          display: "flex",
                          alignItems: "flex-start",
                          gap: 1,
                        }}
                      >
                        <Typography sx={{ fontSize: "12.5px", color: "#DC2626", flex: 1, lineHeight: 1.6 }}>
                          {editError}
                        </Typography>
                        <IconButton
                          size="small"
                          onClick={() => setEditError(null)}
                          sx={{ p: 0, mt: 0.2, color: "#DC2626", flexShrink: 0 }}
                        >
                          <FiX size={13} />
                        </IconButton>
                      </Box>
                    )}
                    <Stack direction="row" spacing={1} justifyContent="flex-end">
                      <Button
                        size="small"
                        onClick={cancelEdit}
                        sx={{
                          textTransform: "none",
                          color: UI.textSub,
                          fontWeight: 600,
                          border: `1px solid ${UI.border}`,
                          borderRadius: "6px",
                        }}
                      >
                        Cancel
                      </Button>
                      <Button
                        size="small"
                        onClick={() => saveEdit(log._id)}
                        sx={{
                          textTransform: "none",
                          background: UI.accent,
                          color: "#fff",
                          fontWeight: 600,
                          borderRadius: "6px",
                          "&:hover": { background: "#0F766E" },
                        }}
                      >
                        Save
                      </Button>
                    </Stack>
                  </Stack>
                ) : (
                  <Stack spacing={1}>
                    <Stack direction="row" justifyContent="space-between" alignItems="center">
                      <Stack direction="row" spacing={1} alignItems="center">
                        <Avatar
                          sx={{
                            width: 24,
                            height: 24,
                            fontSize: "10px",
                            fontWeight: 700,
                            background: UI.accent,
                            color: "#fff",
                          }}
                        >
                          {log.assignedToName?.charAt(0)?.toUpperCase() || "?"}
                        </Avatar>
                        <Typography sx={{ fontSize: "13px", fontWeight: 600, color: UI.text }}>
                          {log.assignedToName || "N/A"}
                        </Typography>
                      </Stack>
                      <Typography sx={{ fontSize: "12px", fontWeight: 600, color: UI.accent }}>
                        {calculateDuration(log.startTime, log.endTime)}
                      </Typography>
                    </Stack>
                    <Stack direction="row" justifyContent="space-between">
                      <Typography sx={{ fontSize: "12px", color: UI.textMuted }}>
                        {formatDisplayDate(log.date)}
                      </Typography>
                      <Typography sx={{ fontSize: "12px", color: UI.textSub, fontVariantNumeric: "tabular-nums" }}>
                        {formatDisplayTime(log.startTime)} – {formatDisplayTime(log.endTime)}
                      </Typography>
                    </Stack>
                    {(nonEmpty(log.assignedByName) || nonEmpty(log.editedByName)) && (
                      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap">
                        {nonEmpty(log.assignedByName) && (
                          <Typography sx={{ fontSize: "11px", color: UI.textMuted }}>
                            Added by {log.assignedByName}
                          </Typography>
                        )}
                        {nonEmpty(log.editedByName) && (
                          <Box
                            sx={{
                              fontSize: "10px",
                              fontWeight: 600,
                              color: UI.edited,
                              background: UI.accentSoft,
                              border: `1px solid ${UI.edited}30`,
                              px: 0.6,
                              py: 0.1,
                              borderRadius: "4px",
                              textTransform: "uppercase",
                              letterSpacing: "0.04em",
                            }}
                          >
                            edited
                          </Box>
                        )}
                      </Stack>
                    )}
                    {canEdit(log) && (
                      <Stack
                        direction="row"
                        spacing={0.5}
                        justifyContent="flex-end"
                        sx={{ pt: 0.5, borderTop: `1px solid ${UI.borderLight}` }}
                      >
                        <IconButton
                          size="small"
                          onClick={() => startEdit(log)}
                          sx={{ color: UI.textSub, "&:hover": { color: UI.accent, background: UI.accentSoft } }}
                        >
                          <FiEdit2 size={14} />
                        </IconButton>
                        <IconButton
                          size="small"
                          onClick={() => requestDelete(log)}
                          sx={{ color: UI.textSub, "&:hover": { color: "#DC2626", background: "#FEE2E2" } }}
                        >
                          <FiTrash2 size={14} />
                        </IconButton>
                      </Stack>
                    )}
                  </Stack>
                )}
              </Box>
            ))}
          </Stack>
        </Box>
      )}

      </Box>{/* end scrollable list */}

      {/* Delete confirmation */}
      <ConfirmDialog
        open={Boolean(confirmTarget)}
        onClose={() => (deleting ? null : setConfirmTarget(null))}
        onConfirm={confirmDelete}
        loading={deleting}
        variant="danger"
        title="Delete this work log?"
        description={
          confirmTarget
            ? `${formatDisplayDate(confirmTarget.date)}, ${formatDisplayTime(confirmTarget.startTime)} – ${formatDisplayTime(confirmTarget.endTime)}. This cannot be undone.`
            : ""
        }
        confirmLabel="Delete"
      />
    </Box>
  );
};

export default WorkLogs;
